import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { STATUS_COLORS } from './colors';
import { formatLocalDate } from '../../utils/dateFormat';

const VERDICT_LABELS = { real: 'Real bug', false_positive: 'False positive', false_positive_fixed: 'FP fixed' };
const VISIBLE_BUGS = 2;
const JIRA_SEARCH_URL = 'https://contentsquare.atlassian.net/issues/?jql=';

// Show the first few bugs as Jira links; the rest collapse into a "+N" chip that opens them all in Jira.
function BugLinks({ bugs }) {
  if (!bugs.length) return <span className="sense-shield-muted">—</span>;
  const shown = bugs.slice(0, VISIBLE_BUGS);
  const extra = bugs.length - shown.length;
  const allKeysUrl = `${JIRA_SEARCH_URL}${encodeURIComponent(`key in (${bugs.map((b) => b.bug).join(',')})`)}`;
  return (
    <div className="sense-shield-bug-links">
      {shown.map((b) => (
        <a
          key={b.bug}
          className={`sense-shield-ticket-link sense-shield-ticket-${b.kind}`}
          href={b.bugUrl}
          target="_blank"
          rel="noreferrer"
          title={VERDICT_LABELS[b.kind]}
        >
          {b.bug}
        </a>
      ))}
      {extra > 0 && (
        <a
          className="sense-shield-bug-more"
          href={allKeysUrl}
          target="_blank"
          rel="noreferrer"
          title={`Open all ${bugs.length} bugs in Jira`}
        >
          +{extra}
        </a>
      )}
    </div>
  );
}

// Evaluation Runs filtered to one prompt over the period picked on this page (calendar days).
function runsUrl(promptId, dateRange) {
  const params = new URLSearchParams({ promptId });
  if (dateRange?.startDate && dateRange?.endDate) {
    params.set('from', formatLocalDate(dateRange.startDate));
    params.set('to', formatLocalDate(dateRange.endDate));
  }
  return `/sense-shield/evaluation-runs?${params.toString()}`;
}

// Closed shield-improvement tickets: Shield was corrected for this prompt.
function FixedLinks({ fixed }) {
  if (!fixed.length) return <span className="sense-shield-muted">—</span>;
  const shown = fixed.slice(0, VISIBLE_BUGS);
  const extra = fixed.length - shown.length;
  const allKeysUrl = `${JIRA_SEARCH_URL}${encodeURIComponent(`key in (${fixed.map((f) => f.bug).join(',')})`)}`;
  return (
    <div className="sense-shield-bug-links">
      {shown.map((f) => (
        <a
          key={f.bug}
          className="sense-shield-ticket-link sense-shield-ticket-fixed"
          href={f.bugUrl}
          target="_blank"
          rel="noreferrer"
          title={`False positive fixed ${f.fixedDaysAgo === 0 ? 'today' : `${f.fixedDaysAgo} d ago`}`}
        >
          {f.bug}
        </a>
      ))}
      {extra > 0 && (
        <a className="sense-shield-bug-more" href={allKeysUrl} target="_blank" rel="noreferrer" title={`Open all ${fixed.length} fixed false positives in Jira`}>
          +{extra}
        </a>
      )}
    </div>
  );
}

function PromptHealthTable({ data, dateRange }) {
  const [pyramidLayer, setPyramidLayer] = useState('smoke');
  const rows = data || [];

  const filteredRows = rows.filter((r) => {
    const promptId = r.promptId || '';
    if (pyramidLayer === 'smoke') {
      return promptId.startsWith('smk-');
    } else if (pyramidLayer === 'e2e') {
      return !promptId.startsWith('smk-');
    }
    return true;
  });

  const maxDays = Math.max(1, ...filteredRows.map((r) => r.oldestOpenDays || 0));

  return (
    <div>
      <div className="sense-shield-panel-title">Prompt Health</div>
      <p className="sense-shield-panel-description">
        Outcome rates per prompt (worst first), with its open Jira tickets (real bugs and false positives to fix), the false positives already fixed, and how long the oldest open ticket has been open
      </p>
      <div className="sense-shield-panel-filters" style={{ marginBottom: 16 }}>
        <div className="sense-shield-filter-group">
          <label className="sense-shield-filter-label">E2E Pyramid Layer:</label>
          <div className="sense-shield-call-type-buttons">
            {['smoke', 'e2e'].map((layer) => (
              <button
                key={layer}
                className={`sense-shield-call-type-btn ${pyramidLayer === layer ? 'active' : ''}`}
                onClick={() => setPyramidLayer(layer)}
                type="button"
              >
                {layer === 'smoke' ? 'Smoke' : 'E2E'}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="sense-shield-panel-content sense-shield-table-wrapper">
        <table className="sense-shield-table">
          <thead>
            <tr>
              <th>Prompt ID</th>
              <th>Pass %</th>
              <th>Fail %</th>
              <th>Warn %</th>
              <th>Open tickets</th>
              <th>Verdict</th>
              <th>Fixed false positives</th>
              <th>Open for</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((r) => (
              <tr key={r.promptId}>
                <td>{r.promptId}</td>
                <td>{r.passRate}</td>
                <td>{r.failRate}</td>
                <td>{r.warningRate}</td>
                <td><BugLinks bugs={r.bugs || []} /></td>
                <td>
                  {(r.verdicts || []).length > 0 ? (
                    <div className="sense-shield-bug-links">
                      {r.verdicts.map((v) => (
                        <span key={v} className={`sense-shield-verdict sense-shield-verdict-${v}`}>{VERDICT_LABELS[v]}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="sense-shield-muted">—</span>
                  )}
                </td>
                <td><FixedLinks fixed={r.fixedFalsePositives || []} /></td>
                <td>
                  {r.oldestOpenDays != null ? (
                    <div className="sense-shield-age-cell">
                      <div
                        className="sense-shield-age-bar"
                        style={{
                          width: `${(r.oldestOpenDays / maxDays) * 70}%`,
                          background: r.oldestOpenDays > 90 ? STATUS_COLORS.fail : STATUS_COLORS.warning,
                        }}
                      />
                      <span>{r.oldestOpenDays} d</span>
                    </div>
                  ) : (
                    <span className="sense-shield-muted">—</span>
                  )}
                </td>
                <td>
                  <Link
                    className="sense-shield-details-link"
                    to={runsUrl(r.promptId, dateRange)}
                    title={`See every run of ${r.promptId} in the selected period`}
                    aria-label={`View runs for ${r.promptId}`}
                  >
                    View runs <ArrowRight size={14} aria-hidden="true" />
                  </Link>
                </td>
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr><td colSpan={9} className="sense-shield-empty">No data for the selected layer</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PromptHealthTable;
